import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Downshift from 'downshift';
import { useTranslation } from 'react-i18next';

import Input from '../Input';
import Checkbox from '../Checkbox';
import Button from '../Button';
import Loader from '../Loader';

import { ReactComponent as DropTickIcon } from 'icons/drop-tick.svg';
import { ReactComponent as DelIcon } from 'icons/del.svg';
import { ReactComponent as TrashIcon } from 'icons/trash.svg';
import { ReactComponent as CreateIcon } from 'icons/create.svg';

import s from './Select.module.css';

const Select = ({
  theme,
  className,
  items,
  fixedWidth,
  name,
  error,
  label,
  placeholder,
  disabled,
  resettable,
  onChange,
  activeChange,
  loading,
  stretch,
  required,
  newLabel,
  onAdd,
  onFilter,
  colored,
  value,
  labelColor,
  selectedItem,
  maxHeight,
  icon,
}) => {
  const listRef = useRef();
  const initial = useRef(true);
  const [t] = useTranslation();
  const [remoteItems, setRemoteItems] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [fetching, setFetching] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isActive, setActive] = useState(false);
  const [isNewActive, setNewActive] = useState(false);

  const getLabel = useCallback(item => item && (typeof item.label === 'function' ? item.label(t) : item.label), [t]);

  const isAsync = typeof items === 'function';
  const allItems = isAsync ? remoteItems : items;

  const isEmpty = value => value === null || value === undefined || value === '' || Number.isNaN(value);

  const findItem = value => !isEmpty(value) && allItems.find(item => item.value === value);

  // downshift needs object if we control selected item so even if the item is undefined we use empty obj {}
  const currentItem = selectedItem || findItem(value);

  const loadItems = useCallback(async () => {
    const text = filterValue || '';
    const filled = text.length > 0 && (!currentItem || getLabel(currentItem) !== text);

    if (filled) {
      setFetching(true);
      const list = await items(filterValue);
      setRemoteItems(list);
      setFetching(false);
    }
    setOpen(filled);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, filterValue, items]);

  useEffect(() => {
    if (isAsync && currentItem) setFilterValue(getLabel(currentItem));
  }, [currentItem, getLabel, isAsync]);

  useEffect(() => {
    if (!isOpen) listRef.current.scrollTop = 0;
  }, [isOpen]);

  // async loading/filtering items
  useEffect(() => {
    if (!isAsync) return;

    if (initial.current) {
      initial.current = false;
      return;
    }

    const handler = setTimeout(loadItems, 300);
    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, isAsync]);

  const selectItem = item => {
    if (item && item.type !== 'checkbox') setOpen(false);

    setFilterValue(item && getLabel(item));

    if (item && item.onClick) {
      item.onClick(item);
    } else {
      const value = item && !isEmpty(item.value) ? item.value : item;
      onChange(value, item);
    }
  };

  const onActiveChange = status => {
    setActive(status);
    activeChange(status);
  };

  return (
    <Downshift
      itemToString={item => (item ? item.value : '')}
      isOpen={isOpen}
      onOuterClick={() => setOpen(false)}
      selectedItem={currentItem || {}}
    >
      {({ getToggleButtonProps, getInputProps, getItemProps, getMenuProps, isOpen, inputValue, highlightedIndex }) => (
        <div className={cn(s.root, className, s[theme], { [s.active]: isActive })}>
          {isAsync ? (
            <Input
              {...getInputProps({
                theme,
                fixedWidth,
                disabled,
                placeholder,
                required,
                rightIcon: fetching && <Loader />,
                value: filterValue,
                className: cn(s.toggle, { [s.active]: isOpen }),
                onKeyDown: ({ keyCode }) => {
                  if (keyCode === 13 && allItems[highlightedIndex]) selectItem(allItems[highlightedIndex]);
                },
              })}
              onChange={value => {
                setFilterValue(value);
                onFilter(value);
              }}
            />
          ) : (
            <Button
              {...getToggleButtonProps({
                theme,
                stretch,
                fixedWidth,
                disabled,
                onClick: () => setOpen(!isOpen),
                loading: loading,
                active: isOpen,
                background: colored && currentItem && currentItem.color,
                className: cn(s.toggle, { [s.active]: isOpen }),
              })}
            >
              <span className={s.value} style={{ color: labelColor }}>
                {label || getLabel(currentItem) || placeholder}&nbsp;
                {/* hack for increasing button width based on dropdown longest item */}
                {theme === 'green' &&
                  allItems.map((item, index) => (
                    <span key={index} className={s.invisible}>
                      {getLabel(item)}
                    </span>
                  ))}
              </span>
              {(!resettable || !currentItem) && <DropTickIcon color={labelColor} />}
              {currentItem && resettable && (
                <DelIcon
                  onClick={e => {
                    e.stopPropagation();
                    selectItem(undefined);
                    setOpen(false);
                  }}
                />
              )}
            </Button>
          )}
          <input type="hidden" name={name} defaultValue={inputValue} />
          <div
            {...getMenuProps({
              className: cn(s.dropdown, { [s.visible]: isOpen, [s.withNew]: !!newLabel }),
              onAnimationStart: ({ animationName }) => animationName === s.expanding && onActiveChange(true),
              onAnimationEnd: ({ animationName }) => animationName === s.collapsing && onActiveChange(false),
            })}
          >
            <ul className={s.list} ref={listRef} style={{ maxHeight: maxHeight && `${maxHeight}px` }}>
              {allItems.length === 0 && <li className={cn(s.item, s.empty)}>{t('Ничего не найдено')}</li>}
              {allItems.map((item, index) => (
                <li
                  {...getItemProps({
                    key: index,
                    index,
                    item,
                    className: cn(s.item, {
                      [s.colored]: !!item.color,
                      [s.selected]: currentItem === item,
                      [s.highlight]: highlightedIndex === index,
                    }),
                  })}
                  onClick={e => {
                    // fix issue with putting select inside <label> (see Field)
                    e.preventDefault();
                    selectItem(item);
                  }}
                >
                  {item.type === 'checkbox' && <Checkbox className={s.checkbox} checked={item.checked} />}
                  {item.deletable && (
                    <TrashIcon
                      className={s.del}
                      onClick={e => {
                        e.stopPropagation();
                        item.onDelete();
                      }}
                    />
                  )}
                  <span className={s.itemLabel} style={item.color && { background: item.color }}>
                    {item.main && item.secondary ? (
                      <>
                        <span className={s.itemLabelMain}>{item.main}</span>
                        <span className={s.itemLabelSecondary}>{item.secondary}</span>
                      </>
                    ) : (
                      getLabel(item)
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {newLabel && (
              <div
                className={s.new}
                onMouseEnter={() => setNewActive(true)}
                onMouseLeave={() => setNewActive(false)}
                onClick={() => {
                  setOpen(false);
                  onAdd();
                }}
              >
                <Button theme="green" icons="only" center active={isNewActive}>
                  <CreateIcon />
                </Button>
                <span className={s.newLabel}>{newLabel}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Downshift>
  );
};

Select.propTypes = {
  items: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  selectedItem: PropTypes.object,
  colored: PropTypes.bool,
  loading: PropTypes.bool,
  stretch: PropTypes.bool,
  newLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onChange: PropTypes.func,
  activeChange: PropTypes.func,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  resettable: PropTypes.bool,
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fixedWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  labelColor: PropTypes.string,
  theme: PropTypes.oneOf(['green', 'toolbar', 'toolbar-white', 'field', 'white']),
};

Select.defaultProps = {
  theme: 'field',
  className: '',
  onChange: () => {},
  onAdd: () => {},
  activeChange: () => {},
  onFilter: () => {},
  placeholder: undefined,
  defaultValue: undefined,
  fixedWidth: undefined,
  maxHeight: undefined,
  labelColor: undefined,
  label: undefined,
  newLabel: undefined,
  disabled: false,
  error: false,
  loading: false,
  stretch: false,
  resettable: false,
  colored: false,
};

export default Select;
